import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import type { LucideIcon } from "lucide-react";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import {
  TXT,
  font,
  motionAllowed,
  soft,
  tone,
  chipHeight,
  type Pick,
  type Scene,
  type Tone,
} from "features/onboarding/art/kit";

export const ArtPlayback = createContext<{
  onComplete: () => void;
  onProgress: (progress: number) => void;
} | null>(null);

// The drawing kit's components: the canvas (which runs a scene while it is on
// screen) and the shapes the illustrations are built from. Helpers, the
// palette and the timeline helpers live in kit.ts.

gsap.registerPlugin(useGSAP);

/** Multiplied opacity of an element and its ancestors. */
function shown(el: Element): number {
  let o = 1;
  for (let e: Element | null = el; e && o > 0; e = e.parentElement) {
    const s = getComputedStyle(e);
    o *= s.visibility === "hidden" ? 0 : Number(s.opacity);
  }
  return o;
}

/**
 * The canvas. The illustration is decorative next to the onboarding copy, so
 * it is hidden from assistive technology. `fill` makes it fit a fixed-height
 * stage instead of following the width.
 */
export function Art({
  id,
  w,
  h,
  scene,
  fill = false,
  play = true,
  label,
  children,
}: {
  id: string;
  w: number;
  h: number;
  scene?: Scene;
  fill?: boolean;
  /** Pauses the loop without leaving the page (e.g. an unselected card). */
  play?: boolean;
  /** Describes the drawing when it carries meaning (otherwise it is hidden). */
  label?: string;
  children: ReactNode;
}) {
  const playback = useContext(ArtPlayback);
  const playbackRef = useRef(playback);
  playbackRef.current = playback;
  const ref = useRef<SVGSVGElement>(null);
  const [on, setOn] = useState(false);
  // The timeline is built the first time the art is on screen.
  const [armed, setArmed] = useState(false);
  const loop = useRef<gsap.core.Timeline | null>(null);
  const intro = useRef<gsap.core.Timeline | null>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setOn(entry.isIntersecting),
      { rootMargin: "0px 0px -10% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (on && play) setArmed(true);
  }, [on, play]);

  useGSAP(
    () => {
      if ((!armed && !playback) || !scene || !motionAllowed()) return;
      const $: Pick = (k) =>
        gsap.utils.toArray<Element>(
          k.endsWith("*") ? `[data-k^="${k.slice(0, -1)}"]` : `[data-k="${k}"]`,
          ref.current
        );
      const tl = gsap.timeline({ paused: true, repeat: playback ? 0 : -1 });
      const rest = scene(tl, $);
      // Show the rest state (the markup) until the loop starts.
      if (playback) {
        const completeAt = rest + 1.2;
        tl.addPause(completeAt, () => playbackRef.current?.onComplete());
        tl.eventCallback("onUpdate", () => {
          playbackRef.current?.onProgress(Math.min(1, tl.time() / completeAt));
        });
        tl.seek(0, true);
      } else {
        tl.seek(rest, false);
      }
      loop.current = tl;
      const inTl = gsap.timeline({ paused: true });
      gsap.utils.toArray<SVGElement>("[data-in]", ref.current).forEach((el) => {
        inTl.fromTo(
          el,
          { opacity: 0, y: 10 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "expo.out",
            immediateRender: false,
          },
          Number(el.dataset.in) || 0
        );
      });
      intro.current = inTl;
    },
    { scope: ref, dependencies: [playback ? true : armed] }
  );

  useEffect(() => {
    const tl = loop.current;
    if (!tl || !ref.current) return;
    if (!on || !play) {
      tl.pause();
      if (!play) started.current = false;
      return;
    }
    if (!started.current) {
      started.current = true;
      // Not visible yet (e.g. still fading in): build it up from empty.
      // Already on screen: carry on from the rest state.
      if (playback) {
        tl.seek(0, true);
      } else if (shown(ref.current) < 0.35) {
        intro.current?.play(0);
        tl.seek(0, false);
      }
    }
    tl.play();
  }, [on, play, armed]);

  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${w} ${h}`}
      className={fill ? "ak-canvas ak-fill" : "ak-canvas"}
      preserveAspectRatio="xMidYMid meet"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : "true"}
      focusable="false"
      data-on={String(on && play)}
    >
      <defs>
        {/* Each card's own box plus room for the blur (3 × 8px, 4px lower),
            not the whole canvas: the filter is redrawn whenever the scene
            moves, so its area is what it costs. */}
        <filter
          id={`${id}-shadow`}
          x="-40%"
          y="-80%"
          width="180%"
          height="260%"
        >
          {/* A hint of lift only: the cards keep their hairline. */}
          <feDropShadow
            dx="0"
            dy="4"
            stdDeviation="8"
            floodColor="var(--viz-shadow)"
            floodOpacity="0.07"
          />
        </filter>
        <filter id={`${id}-lift`} x="-50%" y="-50%" width="200%" height="220%">
          <feDropShadow
            dx="0"
            dy="6"
            stdDeviation="7"
            floodColor="var(--viz-shadow)"
            floodOpacity="0.12"
          />
        </filter>
        <linearGradient id={`${id}-area`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="var(--viz-accent)" stopOpacity="0.18" />
          <stop offset="1" stopColor="var(--viz-accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {children}
    </svg>
  );
}

/**
 * Picks a canvas by the container's width: `wide` from `min` pixels up,
 * `narrow` below. Both render, so the right one paints first; only the visible
 * one runs its loop (a hidden canvas never intersects).
 */
export function ArtSwitch({
  wide,
  narrow,
  min = 500,
}: {
  wide: ReactNode;
  narrow: ReactNode;
  min?: number;
}) {
  return (
    <SwitchRoot $min={min}>
      <div className="ak-wide">{wide}</div>
      <div className="ak-narrow">{narrow}</div>
    </SwitchRoot>
  );
}

const SwitchRoot = styled.div<{ $min: number }>`
  container-type: inline-size;
  width: 100%;
  > .ak-narrow {
    display: none;
  }
  @container (max-width: ${(p) => p.$min - 0.02}px) {
    > .ak-wide {
      display: none;
    }
    > .ak-narrow {
      display: block;
    }
  }
`;

/* ------------------------------------------------------------------------ */
/* Shapes (rest state)                                                      */
/* ------------------------------------------------------------------------ */

/** Card with a hairline border and the shared soft shadow. */
export function Card({
  id,
  x,
  y,
  w,
  h,
  r = 16,
  k,
  children,
}: {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  r?: number;
  k?: string;
  children?: ReactNode;
}) {
  return (
    <g data-k={k}>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={r}
        fill="var(--viz-card)"
        stroke="var(--viz-card-line)"
        filter={`url(#${id}-shadow)`}
      />
      {children}
    </g>
  );
}

/** App window: card with the three dots and a title bar. */
export function Window({
  id,
  x,
  y,
  w,
  h,
  title,
  children,
}: {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  title?: string;
  children?: ReactNode;
}) {
  return (
    <g>
      <g data-in="0">
        <rect
          x={x}
          y={y}
          width={w}
          height={h}
          rx={18}
          fill="var(--viz-card)"
          stroke="var(--viz-card-line)"
          filter={`url(#${id}-shadow)`}
        />
        <line
          x1={x}
          x2={x + w}
          y1={y + 38}
          y2={y + 38}
          stroke="var(--viz-card-line)"
        />
        {[0, 1, 2].map((i) => (
          <circle
            key={i}
            cx={x + 20 + i * 14}
            cy={y + 19}
            r={4}
            fill="var(--viz-ink-soft)"
            stroke="var(--viz-card-line)"
          />
        ))}
        {title ? (
          <text
            x={x + 72}
            y={y + 23}
            className={`${TXT.ink2} ${TXT.num}`}
            style={font(11.5, 500)}
          >
            {title}
          </text>
        ) : null}
      </g>
      {children}
    </g>
  );
}

/** Outlined panel inside a window, with a label and an optional value. */
export function Tile({
  x,
  y,
  w,
  h,
  label,
  value,
  vt,
  vk,
  d = 0.1,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  label?: string;
  value?: string;
  vt?: Tone;
  vk?: string;
  d?: number;
}) {
  return (
    <g data-in={d}>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={12}
        fill="none"
        stroke="var(--viz-rule)"
      />
      {label ? (
        <text x={x + 14} y={y + 22} className={TXT.label}>
          {label}
        </text>
      ) : null}
      {value ? (
        <text
          data-k={vk}
          x={x + w - 14}
          y={y + 22}
          textAnchor="end"
          fill={vt ? tone(vt) : undefined}
          className={vt ? TXT.num : `${TXT.ink} ${TXT.num}`}
          style={font(12.5, vt ? 700 : 600)}
        >
          {value}
        </text>
      ) : null}
    </g>
  );
}

/** Label chip: soft fill, colored text, centered on (x, y). */
export function Chip({
  x,
  y,
  text,
  t = "accent",
  w,
  k,
  solid = false,
  sm = false,
}: {
  x: number;
  y: number;
  text: string;
  t?: Tone;
  w?: number;
  k?: string;
  solid?: boolean;
  /** The lighter 20px chip for thumbnails. */
  sm?: boolean;
}) {
  const h = chipHeight(sm);
  const width =
    w ?? Math.round(text.length * (sm ? 6.4 : 7.2) + (sm ? 18 : 22));
  return (
    <g data-k={k}>
      <rect
        x={x - width / 2}
        y={y - h / 2}
        width={width}
        height={h}
        rx={h / 2}
        fill={solid ? tone(t) : soft(t)}
        stroke={tone(t)}
        strokeOpacity={solid ? 1 : 0.35}
      />
      <text
        x={x}
        y={y + (sm ? 3.6 : 4)}
        textAnchor="middle"
        fill={solid ? "var(--viz-on-solid)" : tone(t)}
        style={{ ...font(sm ? 10.5 : 11.5, 600), letterSpacing: "0.03em" }}
      >
        {text}
      </text>
    </g>
  );
}

/** Order marker on the price line. */
export function Marker({
  x,
  y,
  t = "accent",
  k,
  r = 6.5,
}: {
  x: number;
  y: number;
  t?: Tone;
  k?: string;
  r?: number;
}) {
  return (
    <circle
      data-k={k}
      cx={x}
      cy={y}
      r={r}
      fill="var(--viz-card)"
      stroke={tone(t)}
      strokeWidth={2.5}
    />
  );
}

/** Price stroke. pathLength 1 so the scene can draw it with the dash offset. */
export function Line({
  d,
  t = "accent",
  w = 2.4,
  k,
  dash,
  opacity,
}: {
  d: string;
  t?: Tone;
  w?: number;
  k?: string;
  dash?: string;
  opacity?: number;
}) {
  return (
    <path
      data-k={k}
      d={d}
      pathLength={dash ? undefined : 1}
      fill="none"
      stroke={tone(t)}
      strokeWidth={w}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray={dash ?? "1 1"}
      strokeDashoffset={0}
      strokeOpacity={opacity}
    />
  );
}

/** Live price head: a soft halo with a dot, placed by `ride`. */
export function Head({
  k,
  x,
  y,
  t = "accent",
}: {
  k: string;
  x: number;
  y: number;
  t?: Tone;
}) {
  return (
    <g data-k={k} transform={`translate(${x} ${y})`}>
      <circle r={10} fill={tone(t)} opacity={0.2} />
      <circle r={5} fill="var(--viz-card)" stroke={tone(t)} strokeWidth={2.5} />
    </g>
  );
}

/** Floating card with an icon chip and one or two lines of text. */
export function Toast({
  id,
  x,
  y,
  w,
  icon: Icon,
  title,
  meta,
  t = "accent",
  k,
  float = true,
}: {
  id: string;
  x: number;
  y: number;
  w?: number;
  icon: LucideIcon;
  title: string;
  meta?: string;
  t?: Tone;
  k?: string;
  float?: boolean;
}) {
  const h = meta ? 58 : 46;
  const width =
    w ??
    Math.round(Math.max(title.length * 7.1, (meta?.length ?? 0) * 6.6) + 72);
  return (
    <g data-k={k}>
      <g className={float ? "ak-float" : undefined}>
        <Card id={id} x={x} y={y} w={width} h={h} r={14}>
          <rect
            x={x + 10}
            y={y + (h - 34) / 2}
            width={34}
            height={34}
            rx={10}
            fill={soft(t)}
          />
          <Icon
            x={x + 18}
            y={y + (h - 18) / 2}
            width={18}
            height={18}
            color={tone(t)}
            strokeWidth={2}
          />
          <text
            x={x + 54}
            y={meta ? y + 25 : y + 28}
            className={`${TXT.ink} ${TXT.num}`}
            style={font(12.5, 600)}
          >
            {title}
          </text>
          {meta ? (
            <text x={x + 54} y={y + 42} className={TXT.label}>
              {meta}
            </text>
          ) : null}
        </Card>
      </g>
    </g>
  );
}

/** Faint horizontal rules for a chart area. */
export function Rules({ x, w, ys }: { x: number; w: number; ys: number[] }) {
  return (
    <g>
      {ys.map((y) => (
        <line
          key={y}
          x1={x}
          x2={x + w}
          y1={y}
          y2={y}
          stroke="var(--viz-rule-soft)"
        />
      ))}
    </g>
  );
}

/** Status in a window's title bar: a pulsing dot and a word. */
export function Status({
  x,
  y,
  text,
  t = "up",
  k,
}: {
  k?: string;
  x: number;
  y: number;
  text: string;
  t?: Tone;
}) {
  const dotX = x - text.length * 6.6 - 12;
  return (
    <g data-in="0.3">
      <circle cx={dotX} cy={y - 4} r={4} fill={tone(t)} className="ak-pulse" />
      <circle cx={dotX} cy={y - 4} r={4} fill={tone(t)} />
      <text
        data-k={k}
        x={x}
        y={y}
        textAnchor="end"
        fill={tone(t)}
        style={font(12, 600)}
      >
        {text}
      </text>
    </g>
  );
}
